/**
 * 結果画面（PatternAiOcrResult）での計算フィールド再計算（#3145 / #3172）。
 *
 * 参照元項目の編集確定・行追加/削除・マスタ照合/辞書変換の反映後に、
 * フォーム値のスナップショットから全計算フィールドを再計算する。
 * 計算エンジン本体（utils/calculation）はサーバと共通の純粋関数で、
 * このファイルは結果画面のフォーム構造（単一値 + 明細行）への適用だけを担う。
 *
 * AI読取ONの計算フィールドは、計算結果が空（空値伝播・エラー含む）のとき
 * 読み取り値（ocr_value）へフォールバックし、通常セルと同じく編集可能にする（設計書 §4.8）。
 */
import dayjs from 'dayjs';
import { type ItemListType, type ItemListChildType } from '../../types/common';
import {
  evaluateFormula,
  resolveCalculationOrder,
  type CalculationAggregateValues,
  type CalculationFieldValues,
  type CalculationOrderField,
} from '../calculation';
import { calculationResultMessage } from '../../constants/calculationResult';
import { parseDateStringSafely } from './fileExportUtils';

/** 日付値を計算・表示用文字列に揃えるときの既定形式（保存時の形式と同じ） */
const DEFAULT_DATE_FORMAT = 'YYYY/MM/DD';

/** 計算セルの表示状態。isCalculated=true のセルは読み取り専用にする */
export interface CalculationCellState {
  /** 計算値を採用しているか（読み取り専用表示の判定に使う） */
  isCalculated: boolean;
  /** 欄の下に赤字で表示するエラーメッセージ。無ければ undefined */
  errorMessage?: string;
  /**
   * 計算エラーの理由（計算エンジンが返した原文）。無ければ undefined。
   * result_data の calculation_error にはこちらを保存し、サーバ側適用（#3171）と
   * 意味を揃える（Webhook / 外部APIのレスポンスにも理由が出るようにする）。
   */
  errorReason?: string;
}

/** セルキー → 表示状態。単一値は itemId、明細セルは arrayCalculationCellKey のキー */
export type CalculationCellStates = Record<string, CalculationCellState>;

/** 明細セルの状態キー。項目名に区切り文字が含まれても衝突しないよう ID ベースで作る */
export const arrayCalculationCellKey = (
  parentId: string,
  rowIndex: number,
  childId: string,
): string => `${parentId}:${rowIndex}:${childId}`;

/** 単一値項目の再計算結果 */
export interface SingleCalculationUpdate {
  itemId: string;
  /** 表示値（計算値、またはフォールバックした読み取り値） */
  value: string;
  /** 項目のデータ型（フォーム値への変換に使う） */
  type?: string;
}

/** 明細セルの再計算結果 */
export interface ArrayCalculationUpdate {
  parentId: string;
  /** dynamicFormData のキー（親項目名） */
  parentValue: string;
  /** 表示上の行 index */
  rowIndex: number;
  childId: string;
  /** dynamicFormData の行内キー（子項目名） */
  childValue: string;
  value: string;
  type?: string;
}

export interface RecalculationResult {
  /** パターンに計算フィールドが1つも無い場合は false（呼び出し側は何もしない） */
  hasCalculatedFields: boolean;
  singleUpdates: SingleCalculationUpdate[];
  arrayUpdates: ArrayCalculationUpdate[];
  /** 全計算セルの表示状態（既存 state を丸ごと置き換える） */
  cellStates: CalculationCellStates;
}

export interface RecalculateParams {
  itemList: ItemListType[];
  /** 単一値項目の現在のフォーム値（itemId → 値）。Date・カンマ付き数値はそのまま渡してよい */
  singleValues: Record<string, unknown>;
  /** 明細の現在のフォーム値（親項目名 → 行配列）。各行は子項目名 → 値、index に元行番号を持つ */
  dynamicFormData: Record<string, Array<Record<string, unknown>>>;
  /** Firestore 保存済みの result_data（フォールバック用 ocr_value の取得元） */
  resultData?: Record<string, unknown>;
}

/** result_data のセルのうち、計算フィールドが参照するフィールド */
interface StoredCalculationCell {
  is_calculated?: boolean;
  calculation_error?: string;
  ocr_value?: unknown;
  value?: unknown;
}

const isArrayGroup = (item: ItemListType): boolean =>
  item.type === 'array' || item.children.length > 0;

const isCalculationField = (field: { is_calculated?: boolean; formula?: string }): boolean =>
  field.is_calculated === true && !!field.formula;

/**
 * item_list に有効な計算フィールドが1つでもあるか。
 * 無ければ結果画面の再計算処理は一切動かさない（#3172）
 */
export const containsCalculatedField = (itemList: ItemListType[]): boolean =>
  itemList.some(
    item =>
      item.is_active === true &&
      (isCalculationField(item) ||
        item.children?.some(child => child.is_active === true && isCalculationField(child))),
  );

/** フォーム値を計算エンジンへ渡す文字列に変換する（Date は保存時と同じ形式の文字列にする） */
const toCalculationValue = (value: unknown, outputFormat?: string): string => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format(outputFormat || DEFAULT_DATE_FORMAT) : '';
  }
  return String(value);
};

/** 計算結果の文字列をフォーム表示値に変換する（数値はカンマ区切り、日付は Date にする） */
export const toFormDisplayValue = (value: string, type?: string): string | number | Date | null => {
  if (type === 'number') {
    if (value === '') return null;
    const num = parseFloat(value.replace(/,/g, ''));
    // 数値にならない計算結果はそのまま表示し、バリデーションでエラーにする
    return isNaN(num) ? value : num.toLocaleString();
  }
  if (type === 'date') {
    if (value === '') return null;
    const parsed = parseDateStringSafely(value);
    return parsed ? parsed.toDate() : null;
  }
  return value;
};

/** 保存済みセルから文字列値を取り出す。文字列・数値以外は空文字 */
const readStoredString = (cell: unknown, key: 'value' | 'ocr_value'): string => {
  if (!cell || typeof cell !== 'object') return '';
  const raw = (cell as StoredCalculationCell)[key];
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') return String(raw);
  return '';
};

/** 保存済みセルから読み取り値（ocr_value）を取り出す。無ければ空文字 */
const readStoredOcrValue = (cell: unknown): string => readStoredString(cell, 'ocr_value');

/**
 * 計算エラーの表示文言を決める（表示の読み替えはこの関数に一本化する）。
 * AI読取ONで読み取り値へフォールバックできたセルは、生のエラー理由ではなく案内文言を出す（設計書 §4.8）。
 * 保存する calculation_error は読み替え前の理由（errorReason）側なので、ここでは表示だけを決める。
 */
const toCalculationErrorMessage = (
  errorReason: string | undefined,
  isCalculated: boolean,
  isOcrTarget: boolean | undefined,
  displayedValue: string,
): string | undefined => {
  if (!errorReason) return undefined;
  const isFallbackToOcr = !isCalculated && isOcrTarget !== false && displayedValue !== '';
  return isFallbackToOcr ? calculationResultMessage.fallbackToOcr : errorReason;
};

/**
 * 1セル分の計算を実行し、フォールバック（設計書 §4.8）を適用する。
 * - 計算結果が非空 → 計算値を採用（読み取り専用）
 * - 空 + AI読取ON → 読み取り値へフォールバック（編集可能）。エラー時は専用文言を表示
 * - 空 + AI読取OFF → 空のまま計算値扱い（読み取り専用）。エラー時はエラー文言を表示
 */
const evaluateWithFallback = (
  formula: string,
  context: CalculationFieldValues,
  isOcrTarget: boolean | undefined,
  ocrValue: string,
  /** 明細の集計（SUM）用の値（#3358）。単一項目の計算でのみ渡す */
  aggregateValues?: CalculationAggregateValues,
): { value: string; state: CalculationCellState } => {
  const result = evaluateFormula(formula, context, aggregateValues);
  if (result.value !== '') {
    return { value: result.value, state: { isCalculated: true } };
  }
  const errorReason = result.error?.message;
  if (isOcrTarget !== false) {
    return {
      value: ocrValue,
      state: {
        isCalculated: false,
        errorReason,
        errorMessage: toCalculationErrorMessage(errorReason, false, isOcrTarget, ocrValue),
      },
    };
  }
  return {
    value: '',
    state: {
      isCalculated: true,
      errorReason,
      errorMessage: toCalculationErrorMessage(errorReason, true, isOcrTarget, ''),
    },
  };
};

/**
 * 読み込んだ result_data から計算セルの初期表示状態を作る。
 * サーバ側適用（#3171）が書き込んだ is_calculated / calculation_error を画面状態に写す。
 */
export const buildCalculationCellStatesFromResultData = (
  itemList: ItemListType[] | undefined,
  resultData: Record<string, unknown> | undefined,
): CalculationCellStates => {
  const states: CalculationCellStates = {};
  if (!itemList) return states;

  const deriveState = (cell: unknown, isOcrTarget: boolean | undefined): CalculationCellState => {
    const stored = (cell ?? {}) as StoredCalculationCell;
    const isCalculated = stored.is_calculated === true;
    const errorReason = stored.calculation_error || undefined;
    if (!errorReason) return { isCalculated };
    return {
      isCalculated,
      errorReason,
      errorMessage: toCalculationErrorMessage(
        errorReason,
        isCalculated,
        isOcrTarget,
        readStoredString(cell, 'value'),
      ),
    };
  };

  itemList
    .filter((item: ItemListType) => item.is_active === true)
    .forEach((item: ItemListType) => {
      if (!isArrayGroup(item)) {
        if (!isCalculationField(item)) return;
        states[item.id] = deriveState(resultData?.[item.id], item.is_ocr_target);
        return;
      }
      const calcChildren = item.children.filter(
        (child: ItemListChildType) => child.is_active === true && isCalculationField(child),
      );
      if (calcChildren.length === 0) return;
      const rows = resultData?.[item.id];
      if (!Array.isArray(rows)) return;
      rows.forEach((row: Record<string, unknown>, rowIndex: number) => {
        calcChildren.forEach((child: ItemListChildType) => {
          states[arrayCalculationCellKey(item.id, rowIndex, child.id)] = deriveState(
            row?.[child.id],
            child.is_ocr_target,
          );
        });
      });
    });

  return states;
};

/**
 * フォーム値のスナップショットから全計算フィールドを再計算する。
 * 評価順は依存関係のトポロジカルソート（単一項目 → 明細は行ごとに同一行の子項目 + 単一項目を参照）。
 */
export const recalculateCalculatedFields = ({
  itemList,
  singleValues,
  dynamicFormData,
  resultData,
}: RecalculateParams): RecalculationResult => {
  const empty: RecalculationResult = {
    hasCalculatedFields: false,
    singleUpdates: [],
    arrayUpdates: [],
    cellStates: {},
  };

  const activeItems = itemList.filter((item: ItemListType) => item.is_active === true);
  const singles = activeItems.filter((item: ItemListType) => !isArrayGroup(item));
  const arrayGroups = activeItems.filter(isArrayGroup);

  const calcSingles = singles.filter(isCalculationField);
  const calcChildrenByGroup = new Map<string, ItemListChildType[]>();
  arrayGroups.forEach((group: ItemListType) => {
    const calcChildren = group.children.filter(
      (child: ItemListChildType) => child.is_active === true && isCalculationField(child),
    );
    if (calcChildren.length > 0) calcChildrenByGroup.set(group.id, calcChildren);
  });

  if (calcSingles.length === 0 && calcChildrenByGroup.size === 0) return empty;

  // 評価順を依存関係から決める。循環参照等はパターン保存時に弾かれているため、
  // ここでエラーになった場合は再計算をスキップする（既存の表示状態を維持する）
  const orderFields: CalculationOrderField[] = [
    ...calcSingles,
    ...Array.from(calcChildrenByGroup.values()).flat(),
  ].map(field => ({ fieldCode: field.field_code ?? field.id, formula: field.formula }));
  const { order, error: orderError } = resolveCalculationOrder(orderFields);
  if (orderError) return empty;

  const orderIndex = new Map<string, number>(order.map((code, index) => [code, index]));
  const sortByOrder = <T extends { field_code?: string; id: string }>(fields: T[]): T[] =>
    [...fields].sort(
      (a, b) =>
        (orderIndex.get(a.field_code ?? a.id) ?? 0) - (orderIndex.get(b.field_code ?? b.id) ?? 0),
    );

  const cellStates: CalculationCellStates = {};
  const singleUpdates: SingleCalculationUpdate[] = [];
  const arrayUpdates: ArrayCalculationUpdate[] = [];

  // 単一値項目のコンテキスト（フィールドコード → 現在値）
  const singleContext: CalculationFieldValues = {};
  singles.forEach((item: ItemListType) => {
    if (!item.field_code) return;
    singleContext[item.field_code] = toCalculationValue(singleValues[item.id], item.outputFormat);
  });

  // 明細は行ごとに「単一値 + 同じ行の子項目」をコンテキストにして計算する（設計書 §4.4）
  arrayGroups.forEach((group: ItemListType) => {
    const calcChildren = calcChildrenByGroup.get(group.id);
    if (!calcChildren) return;
    const activeChildren = group.children.filter(
      (child: ItemListChildType) => child.is_active === true,
    );
    const storedRows = resultData?.[group.id];
    (dynamicFormData[group.parentValue] ?? []).forEach(
      (row: Record<string, unknown>, rowIndex: number) => {
        const rowContext: CalculationFieldValues = { ...singleContext };
        activeChildren.forEach((child: ItemListChildType) => {
          if (!child.field_code) return;
          rowContext[child.field_code] = toCalculationValue(
            row[child.childValue],
            child.outputFormat,
          );
        });
        // フォールバック用の ocr_value は元の行番号（row.index）で保存済みデータから引く
        // （行の追加・削除で表示上の行番号と保存済みデータの行番号がズレるため）
        const originalIndex = typeof row.index === 'number' ? row.index : -1;
        const storedRow =
          Array.isArray(storedRows) && originalIndex >= 0
            ? (storedRows[originalIndex] as Record<string, unknown> | undefined)
            : undefined;
        sortByOrder(calcChildren).forEach((child: ItemListChildType) => {
          const { value, state } = evaluateWithFallback(
            child.formula ?? '',
            rowContext,
            child.is_ocr_target,
            readStoredOcrValue(storedRow?.[child.id]),
          );
          if (child.field_code) rowContext[child.field_code] = value;
          // セル状態のキーは行ID（#3283）。行の挿入・削除で他の行にズレないようにする。
          // arrayUpdates の rowIndex は書き込み先の配列位置なのでそのまま渡す。
          cellStates[
            arrayCalculationCellKey(
              group.id,
              originalIndex >= 0 ? originalIndex : rowIndex,
              child.id,
            )
          ] = state;
          arrayUpdates.push({
            parentId: group.id,
            parentValue: group.parentValue,
            rowIndex,
            childId: child.id,
            childValue: child.childValue,
            value,
            type: child.type,
          });
        });
      },
    );
  });

  /**
   * 明細の集計（SUM）用の値（#3358）。キーは子項目のフィールドコード（`親.子` の形）。
   * 明細の計算が終わってから作るため、計算列（金額＝単価×数量）も合計できる。
   */
  const aggregateContext: CalculationAggregateValues = {};
  arrayGroups.forEach((group: ItemListType) => {
    const rows = dynamicFormData[group.parentValue] ?? [];
    group.children
      .filter((child: ItemListChildType) => child.is_active === true && child.field_code)
      .forEach((child: ItemListChildType) => {
        const code = child.field_code as string;
        aggregateContext[code] = rows.map((row: Record<string, unknown>, rowIndex: number) => {
          // この再計算で更新した値があればそれを使う（計算列を合計できるようにする）
          const updated = arrayUpdates.find(
            u => u.parentId === group.id && u.rowIndex === rowIndex && u.childId === child.id,
          );
          return updated
            ? updated.value
            : toCalculationValue(row[child.childValue], child.outputFormat);
        });
      });
  });

  // 単一値の計算フィールドを評価順に計算する（後続の式から参照できるようコンテキストも更新）
  sortByOrder(calcSingles).forEach((item: ItemListType) => {
    const { value, state } = evaluateWithFallback(
      item.formula ?? '',
      singleContext,
      item.is_ocr_target,
      readStoredOcrValue(resultData?.[item.id]),
      aggregateContext,
    );
    if (item.field_code) singleContext[item.field_code] = value;
    cellStates[item.id] = state;
    singleUpdates.push({ itemId: item.id, value, type: item.type });
  });

  return { hasCalculatedFields: true, singleUpdates, arrayUpdates, cellStates };
};
